import { useEffect, useState, useRef } from "react";
import "../styles/App.css";
import { useTranslation } from "react-i18next";

function BannerSwiper() {
    const { t, i18n } = useTranslation();

    // Helper function to get the image suffix based on language
    const getImageSuffix = () => (i18n.language === "zh-HK" ? "-cn" : "");

    // Helper to determine if it's mobile
    const isMobile = () => typeof window !== "undefined" && window.innerWidth < 768;

    // Initialize slides with correct images before first render
    const getInitialSlides = () => {
        const suffix = getImageSuffix();
        if (isMobile()) {
            return [
                `/Images/banners/one-platform-one-solution-for-personalized-efficient-activity-coordination-mobile${suffix}.jpg`,
                `/Images/banners/unleash-your-kids-potential-with-personalized-activities-mobile${suffix}.png`,
                `/Images/banners/one-stop-activity-matching-platform-mobile${suffix}.png`,
                `/Images/banners/one-platform-one-solution-for-personalized-efficient-activity-coordination-mobile${suffix}.jpg`,
            ];
        } else {
            return [
                `/Images/banners/one-platform-one-solution-for-personalized-efficient-activity-coordination${suffix}.jpeg`,
                `/Images/banners/unleash-your-kids-potential-with-personalized-activities${suffix}.jpeg`,
                `/Images/banners/one-stop-activity-matching-platform${suffix}.jpeg`,
                `/Images/banners/one-platform-one-solution-for-personalized-efficient-activity-coordination${suffix}.jpeg`,
            ];
        }
    };

    const [slides, setSlides] = useState(getInitialSlides);
    const [displayIndex, setDisplayIndex] = useState(0);
    const [enableTransition, setEnableTransition] = useState(true);
    const isForward = useRef(true);

    // Update slides on resize or language change
    useEffect(() => {
        const updateSlides = () => {
            const suffix = getImageSuffix();
            if (window.innerWidth < 768) {
                setSlides([
                    `/Images/banners/one-platform-one-solution-for-personalized-efficient-activity-coordination-mobile${suffix}.jpg`,
                    `/Images/banners/unleash-your-kids-potential-with-personalized-activities-mobile${suffix}.png`,
                    `/Images/banners/one-stop-activity-matching-platform-mobile${suffix}.png`,
                    `/Images/banners/one-platform-one-solution-for-personalized-efficient-activity-coordination-mobile${suffix}.jpg`,
                ]);
            } else {
                setSlides([
                    `/Images/banners/one-platform-one-solution-for-personalized-efficient-activity-coordination${suffix}.jpeg`,
                    `/Images/banners/unleash-your-kids-potential-with-personalized-activities${suffix}.jpeg`,
                    `/Images/banners/one-stop-activity-matching-platform${suffix}.jpeg`,
                    `/Images/banners/one-platform-one-solution-for-personalized-efficient-activity-coordination${suffix}.jpeg`,
                ]);
            }
        };

        updateSlides();
        window.addEventListener("resize", updateSlides);
        return () => window.removeEventListener("resize", updateSlides);
    }, [i18n.language]);

    // Handle infinite scroll snap
    useEffect(() => {
        if (displayIndex === slides.length - 1) {
            const timer = setTimeout(() => {
                setEnableTransition(false);
                setDisplayIndex(0);
                setTimeout(() => setEnableTransition(true), 0);
            }, 200);
            return () => clearTimeout(timer);
        }
    }, [displayIndex, slides.length]);

    const handlePrev = () => {
        if (displayIndex === 0) {
            setEnableTransition(false);
            setDisplayIndex(slides.length - 2);
            setTimeout(() => setEnableTransition(true), 0);
            isForward.current = true;
        } else {
            setDisplayIndex(displayIndex - 1);
        }
    };

    const handleNext = () => {
        if (displayIndex === slides.length - 2) {
            setEnableTransition(false);
            setDisplayIndex(0);
            setTimeout(() => setEnableTransition(true), 0);
            isForward.current = false;
        } else {
            setDisplayIndex(displayIndex + 1);
        }
    };

    const handleDotClick = (index) => {
        isForward.current = index > displayIndex;
        setDisplayIndex(index);
    };

    // Auto swiping
    useEffect(() => {
        const interval = setInterval(() => {
            setDisplayIndex((prevIndex) => {
                if (isForward.current) {
                    if (prevIndex === slides.length - 2) {
                        isForward.current = false;
                        return prevIndex - 1;
                    } else {
                        return prevIndex + 1;
                    }
                } else {
                    if (prevIndex === 0) {
                        isForward.current = true;
                        return prevIndex + 1;
                    } else {
                        return prevIndex - 1;
                    }
                }
            });
        }, 10000);
        return () => clearInterval(interval);
    }, [slides.length]);

    return (
        <div className="swiper">
            <div
                className="swiper-wrapper"
                style={{
                    transform: `translateX(-${displayIndex * 100}%)`,
                    transition: enableTransition ? "transform 0.2s ease-in" : "none",
                }}
            >
                {slides.map((src, i) => (
                    <div className="swiper-slide" key={i}>
                        <img src={src} alt={`${i18n.language === "zh-HK" ? "香港兒童課外活動推薦平台 ActiviKid" : "Hong Kong Kids Activities Recommendation Platform ActiviKid"}`} />
                    </div>
                ))}
            </div>
            {/* Arrows */}
            <button className="swiper-button left" onClick={handlePrev}>〈</button>
            <button className="swiper-button right" onClick={handleNext}>〉</button>
            {/* Dots */}
            <div className="swiper-pagination">
                {slides.slice(0, slides.length - 1).map((_, dotIndex) => (
                    <button
                        key={dotIndex}
                        className={`swiper-dot ${
                            displayIndex % (slides.length - 1) === dotIndex ? "active" : ""
                        }`}
                        onClick={() => handleDotClick(dotIndex)}
                    />
                ))}
            </div>
        </div>
    );
}

export default BannerSwiper;
